import React, { useState, useEffect } from 'react';
import utils from '../utils';
import { LinkContainer } from 'react-router-bootstrap';
import CONSTANTS from '../constants';
import { Col, Row, Button, Form } from 'react-bootstrap';
import IntellFormattedList from './Intelligence/IntellFormattedList';
import FormattedArticleList from './News/FormattedArticleList';
import FilterBarElastic from './Layout/Filters/FilterBarElastic';
import ListPagination from './Layout/ListPagination';
import 'react-loading-skeleton/dist/skeleton.css'
import ListSkeletonLoader from './Layout/ListSkeletonLoader';
import universalElasticConnectedListUtils from './UniversalElasticConnectedListUtils';


function UniversalElasticConnectedList({
  pageType = null,
  reloadLoading = false,
  handleLoading,
  tenantSetting,
  onArticlesRetrieved = null,
  engineName = null,
  searchTerm = null,
  onTotalPagesReceived,
  tagQuery = false,
  numPerPage = null,
  metaData,
  flatList = true,
}) {
  //handles listing / filtering on elastic lists

  const [articles, setArticles] = useState([]);
  const [status, setStatus] = useState('ok');
  const [loading, setLoading] = useState(true);
  const [loadingResults, setLoadingResults] = useState(true);
  const [curPage, setCurPage] = useState(1)
  const [curFilters, setCurFilters] = useState({});
  const [clearFilter, setClearFilter] = useState(false)
  const [curTerm, setCurTerm] = useState();
  const [sortOrder, setSortOrder] = useState("DATE_DESC");
  const [totalNumArticles, setTotalNumArticles] = useState(0);
  let [options, setOptions] = useState({
    'Show Archived': false,
  });

  let tenID = utils.getForceTenantValue();
  const isUrlQueryValid = typeof searchTerm !== 'object' || searchTerm === null || Array.isArray(searchTerm);

  if (!tenID) {
    //if no currently set forceTenantValue, get the id from the agility record, and set it in localstorage
    tenID = tenantSetting.tenant.fields.aDGroupID || null;
    utils.setForceTenantValue(tenID);
  }

  const findSearchObject = (thisquery, numPerCall, page, engine, tenantID, filters = null, start = null, end = null, sortOrder, searchType) => {


    let lbSearchObj = {
      engine: engine,
      is_boolean: tagQuery && tagQuery === true,
      filters: filters ? filters : {},
      size: numPerCall,
      page: page ? page : 1,
      start_date: start ? start : null,
      end_date: end ? end : null,
      sort_order: sortOrder,
      tenant_id: tenantID
    };

    let advancedSearchObj = {
      engine: engine,
      musts: thisquery && thisquery.musts ? thisquery.musts : [],
      shoulds: thisquery && thisquery.shoulds ? thisquery.shoulds : [],
      must_nots: thisquery && thisquery.must_not ? thisquery.must_not : [],
      size: numPerCall,
      page: page ? page : 1,
      sort_order: sortOrder,
      tenant_id: tenantID,
      use_tags: false
      //tags: thisquery && thisquery.tags ? thisquery.tags : [],  --- Backend is not built to handle this yet
    };

    let retObj = isUrlQueryValid === true ? lbSearchObj : advancedSearchObj;
    return retObj;
  }

  const getResults = async (thisquery, numPerCall, page, engine, tenantID, filters = null, showLoading = true, start, end, currentSortOrder) => {
    let search = window.location.search;
    let searchType = universalElasticConnectedListUtils.getSearchType(search);
    let finalSortOrder = currentSortOrder || (searchType === 'tag' ? 'DATE_DESC' : sortOrder);
    let endpoint = isUrlQueryValid === true ? 'lb_search' : 'advanced_search';
    let searchObj = findSearchObject(thisquery, numPerCall, page, engine, tenantID, filters, start, end, finalSortOrder, searchType);

    setLoading(showLoading);


    if (engine === 'linksbridge') {
      searchObj.exclude_filters = { 'category': ['MLPNormal', 'MLPReport'] };
    }

    if (isUrlQueryValid === true && typeof thisquery === 'string') {
      searchObj.query = thisquery;
    }

    let newResponse = await utils.db_api_post(endpoint, process.env.REACT_APP_DB_PUBLIC_KEY, searchObj);
    setArticles(newResponse);
    setTotalNumArticles(newResponse.hit_count);
    onTotalPagesReceived(newResponse.hit_count / numPerCall);
    if (onArticlesRetrieved) {
      onArticlesRetrieved(newResponse.results);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    if (!engineName) {
      console.log('engineName is not set');
      return;
    }

    try {
      // Trigger on first load, reload, or when the search term changes
      if (reloadLoading || loadingResults || searchTerm !== curTerm) {
        setArticles([]);
        setCurPage(1);

        // If current page is not 1, reset it and wait for the next render
        if (curPage !== 1) {
          setCurPage(1);
          return;
        }

        if (searchTerm !== curTerm) {
          setCurTerm(searchTerm);
          setCurFilters((prev) => ({
            ...prev,
            sort_by: { label: "Most recent", value: "DATE_DESC" }, // Always default to date desc
          }));
        }

        // Fetch results
        getResults(
          searchTerm,
          numPerPage,
          1,
          engineName,
          tenID,
          engineName === 'cxreports' ? { archived: 'false' } : '',
          true,
          null,
          null
        );

        setStatus('ok');
        setLoadingResults(false);
        if (reloadLoading) handleLoading(reloadLoading);
      }
    } catch (error) {
      setStatus('error');
      console.error(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadLoading, loadingResults, searchTerm, curTerm, engineName, curPage]);


  // Applies all active filters and triggers a new search
  const applyFiltersHandler = (filterList, num, currentSortOrder) => {
    // Build final filters object for the request
    let filters = universalElasticConnectedListUtils.findFiltersToSet(filterList, engineName, options);
    let start = filterList.from || null;
    let end = filterList.to || null;

    // Determine sort order: from props, filters, or fallback to current state
    const rawSort = currentSortOrder || filterList.sort_by || curFilters.sort_by || sortOrder;
    const newSortOrder = typeof rawSort === 'object' && rawSort.value ? rawSort.value : rawSort;

    // Update state and fetch data
    setSortOrder(newSortOrder);
    getResults(searchTerm, numPerPage, num ?? curPage, engineName, tenID, filters, true, start, end, newSortOrder);
  };


  //renders appropriate data based on filters applied
  // Handles filter changes and clears
  const filteringDataHandler = (field, filter) => {
    setCurPage(1); // Always reset to first page on filter change

    if (field === 'clear-filters') {
      // Clear all filters and reset UI state
      setClearFilter(true);
      setCurFilters({});
      setOptions({ 'Show Archived': false });
      setLoadingResults(true);
    } else {
      // Apply new filter
      setClearFilter(false);
      const tempFilters = { ...curFilters };

      if (field === 'date-range') {
        // Special handling for date range filters
        if (filter.from) tempFilters['from'] = filter.from;
        if (filter.to) tempFilters['to'] = filter.to;
      } else {
        // Generic filter update
        tempFilters[field] = filter;
      }

      setCurFilters(tempFilters);
    }
  };

  const paginate = (num) => {
    setLoading(true)
    setCurPage(num);
    applyFiltersHandler(curFilters, num, sortOrder);
  }

  function selectionHandler(event) {
    let temp = { ...options }
    temp[event.target.name] = event.target.checked
    setOptions(temp)
  }

  return (
    !loading && !loadingResults ?
      status === 'ok' && articles && articles.results ?
        engineName && engineName === CONSTANTS.ELASTIC_ENGINES.CX ?
            <>
              <Row>
                <Col lg={9} className='search-content'>
                  {
                    articles.results.length > 0 ?
                      <>
                        <IntellFormattedList
                          list={articles.results}
                          tenant={tenantSetting}
                          engineName={engineName}
                          searchTerm={isUrlQueryValid === true ? searchTerm : null}
                        />
                        <Row className="pagination-row m-b-end-04">
                          {
                            totalNumArticles > numPerPage ?
                              <>
                                <Col>
                                  <ListPagination
                                    articlesPerPage={numPerPage}
                                    totalArticles={totalNumArticles}
                                    paginate={paginate}
                                    curPage={curPage}
                                  />
                                </Col>
                                <Col>
                                  <span className='report-num'>{`${articles.results.length} Reports`}</span>
                                </Col>
                              </>
                            : ''
                          }
                        </Row>
                      </>
                    : <div>No articles available that match your requests</div>
                  }
                </Col>
                <Col lg={3} className='uni-filter-bar m-b-start'>
                  {
                    pageType && (pageType !== CONSTANTS.ELASTIC_PAGE_TYPES.SEARCH) ?
                      <>
                        <LinkContainer to={'/intelligence/create'}>
                          <Button className="side-col-btn tertiary-button m-b-end-06">Add New Report</Button>
                        </LinkContainer>
                        <label className='subtitle-lg m-i-start-04 m-b-end-03'>More Display Settings</label>
                        <Form.Check 
                          type="checkbox"
                          name="Show Archived"
                          label="Show Archived"
                          checked={options['Show Archived']}
                          onChange={selectionHandler}
                          className='m-i-start-04 m-b-end-03'
                        />
                      </>
                    : null
                  }
                  <FilterBarElastic
                    //metaData refers to filter to display based on app
                    metaData={metaData}
                    data={articles.results}
                    filteringDataHandler={filteringDataHandler}
                    rowsPerPage={10}
                    curFilters={curFilters}
                    applyFiltersHandler={applyFiltersHandler}
                    clearFilter={clearFilter}
                    apiName={'intel-filter'}
                  />
                </Col>
              </Row>
            </>
        :
          engineName === CONSTANTS.ELASTIC_ENGINES.PUBLIC ?
              <>
                <Row>
                  <Col lg={9} className='search-content'>
                    {
                      articles.results.length > 0 ?
                        <>
                          <FormattedArticleList
                            engineName={engineName}
                            list={articles.results}
                            tenant={tenantSetting}
                            flatList={flatList}
                            searchTerm={isUrlQueryValid === true ? searchTerm : null}
                          />
                          <Row className="pagination-row m-b-end-04">
                            {
                              totalNumArticles > numPerPage ?
                                <ListPagination
                                  articlesPerPage={numPerPage}
                                  totalArticles={totalNumArticles}
                                  paginate={paginate}
                                  curPage={curPage}
                                />
                                : ''
                            }
                          </Row>
                        </>
                      : <div>No articles available that match your requests</div>
                    }
                  </Col>
                  <Col lg={3} className='uni-filter-bar m-b-start'>
                    <FilterBarElastic
                      //metaData refers to filter to display based on app
                      metaData={metaData}
                      data={articles.results}
                      filteringDataHandler={filteringDataHandler}
                      curFilters={curFilters}
                      applyFiltersHandler={applyFiltersHandler}
                      apiName={'news-filter'}
                      clearFilter={clearFilter}
                      className='m-b-start'
                    />
                  </Col>
                </Row>
              </>
          : null
        : status === 'error'
          ? <div>An error occurred while loading articles. Please try again.</div>
          : <ListSkeletonLoader preview={false} />
      : <ListSkeletonLoader preview={false} />
  )
}

export default UniversalElasticConnectedList